import {
    type AnyPermission,
    type MedicalPermissionSection,
    MedicalSections,
    type ProfilePermissions,
    type Section,
    type TeamPermissions,
} from "@qendo/permissions";
import {
    type ContactableProfileRole,
    ContactableProfileRoles,
    ProfileRole,
    type SupportProfileRole,
    SupportProfileRoles,
    type TeamProfileRole,
    TeamProfileRoles,
    type UserBackedProfileRole,
} from "@qendo/profile";
import type { EmergencyContact } from "@qendo/profile/contacts";
import type { ProfileInvitationData } from "@qendo/profile/invitation";
import type { ShareMemberData, TeamLike, TeamMemberData, UserMemberData } from "@qendo/profile/team";
import { filled } from "~/util";

export function isTeam(section: Section): section is "team" {
    return section === "team";
}

export function isMedical(section: Section): section is MedicalPermissionSection {
    return MedicalSections.includes(section as MedicalPermissionSection);
}

export function usesMedical(section: Section, permission: AnyPermission): boolean {
    return isMedical(section) && permission.view === null && permission.update === null;
}

export function hasAnyAbility(permission: AnyPermission): boolean {
    return Object.values(permission).some((v) => v);
}

export function isDifferentMedical(section: Section, permissions: ProfilePermissions): boolean {
    return (
        isMedical(section) &&
        !usesMedical(section, permissions[section]) &&
        permissionsDiffer(permissions[section], permissions.medical)
    );
}

export function hasCustomMedical(permissions: ProfilePermissions): boolean {
    return MedicalSections.some((section) => isDifferentMedical(section, permissions));
}

export function permissionsMatch(a: AnyPermission, b: AnyPermission) {
    return (
        a.view === b.view &&
        a.update === b.update &&
        (a as TeamPermissions).assign_owner === (b as TeamPermissions).assign_owner
    );
}

export function permissionsDiffer(a: AnyPermission, b: AnyPermission) {
    return !permissionsMatch(a, b);
}

export function isUserRole(role?: ProfileRole | null): role is TeamProfileRole {
    return TeamProfileRoles.includes(role as TeamProfileRole);
}

export function isSupportRole(role?: ProfileRole | null): role is SupportProfileRole {
    return SupportProfileRoles.includes(role as SupportProfileRole);
}

export function isContactableRole(role?: ProfileRole | null): role is ContactableProfileRole {
    return ContactableProfileRoles.includes(role as SupportProfileRole);
}

export function isInvitableRole(role?: ProfileRole | null): role is ContactableProfileRole {
    return TeamProfileRoles.includes(role as TeamProfileRole) || role === ProfileRole.contact;
}

export function isUserBackedRole(role?: ProfileRole | null): role is UserBackedProfileRole {
    return TeamProfileRoles.includes(role as TeamProfileRole) || role === ProfileRole.share;
}

export function isUserBackedMember(member?: TeamLike | null): member is UserMemberData {
    return filled(member) && Object.hasOwn(member, "user");
}
export function isTeamMember(member?: TeamLike | null): member is TeamMemberData {
    return isUserBackedMember(member) && isUserRole(member.role);
}

export function isShare(member?: TeamLike | null): member is ShareMemberData {
    return isUserBackedMember(member) && member.role === ProfileRole.share;
}

export function isInvitation(member?: TeamLike | null): member is ProfileInvitationData {
    return filled(member) && isUserRole(member.role) && !isTeamMember(member);
}

export function isContact(member?: TeamLike | null): member is ProfileInvitationData {
    return filled(member) && member.role === ProfileRole.contact;
}

export function isEmergencyContact(member?: TeamLike | null): member is TeamLike & EmergencyContact {
    return filled(member?.emergency_contact);
}
